import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";

export function useStores() {
  return useQuery({
    queryKey: ["stores"],
    queryFn: () => fetchJson<any[]>("/api/stores"),
  });
}

export function useStore(id: string | null) {
  return useQuery({
    queryKey: ["stores", id],
    queryFn: () => fetchJson<any>(`/api/stores/${id}`),
    enabled: !!id,
  });
}

export function useCreateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any) => fetchJson<{ id: string }>("/api/stores", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stores"] }),
  });
}

export function useUpdateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: any) => fetchJson(`/api/stores/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stores"] }),
  });
}

export function useDeleteStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetchJson(`/api/stores/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stores"] }),
  });
}

export function useTestConnection() {
  return useMutation({
    mutationFn: (body: any) => fetchJson("/api/stores/test-connection", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  });
}

export type StoreSyncStatus = {
  syncMode: "api" | "webhook";
  backfillStatus: "running" | "done" | "error" | null;
  lastSyncedAt: string | null;
  counts: { orders: number; products: number; customers: number };
  failedEvents: number;
};

export function useStoreSyncStatus(storeId: string | null) {
  return useQuery({
    queryKey: ["stores", storeId, "sync-status"],
    queryFn: () => fetchJson<StoreSyncStatus>(`/api/stores/${storeId}/sync/status`),
    enabled: !!storeId,
    refetchInterval: (query) =>
      query.state.data?.backfillStatus === "running" ? 2000 : false,
  });
}

export function useEnableSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (storeId: string) => fetchJson(`/api/stores/${storeId}/sync/enable`, { method: "POST" }),
    onSuccess: (_d, storeId) => {
      qc.invalidateQueries({ queryKey: ["stores", storeId, "sync-status"] });
      qc.invalidateQueries({ queryKey: ["stores"] });
    },
  });
}

export function useDisableSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (storeId: string) => fetchJson(`/api/stores/${storeId}/sync/disable`, { method: "POST" }),
    onSuccess: (_d, storeId) => {
      qc.invalidateQueries({ queryKey: ["stores", storeId, "sync-status"] });
      qc.invalidateQueries({ queryKey: ["stores"] });
    },
  });
}

export function useResyncStore(storeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => fetchJson(`/api/stores/${storeId}/sync/resync`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stores", storeId, "sync-status"] });
      qc.invalidateQueries({ queryKey: ["stores"] });
    },
  });
}

export type WebhookRefreshResult = {
  deliveryUrl: string;
  updated: number;
  created: number;
  recreated: number;
  failed: { topic: string; error: string }[];
};

export function useRefreshWebhooks(storeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opts?: { skipPreflight?: boolean }) =>
      fetchJson<WebhookRefreshResult>(
        `/api/stores/${storeId}/sync/refresh-webhooks${opts?.skipPreflight ? "?skipPreflight=1" : ""}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stores", storeId, "sync-status"] });
    },
  });
}

export type FullResyncResult = {
  deletedOrders: number;
  deletedProducts: number;
  deletedCustomers: number;
  customers: number;
};

export function useFullResyncStore(storeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => fetchJson<FullResyncResult>(`/api/stores/${storeId}/sync/full-resync`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stores", storeId, "sync-status"] });
      qc.invalidateQueries({ queryKey: ["stores"] });
    },
  });
}

export function useUploadLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const form = new FormData();
      form.append("logo", file);
      return fetchJson<{ logoUrl: string }>(`/api/stores/${id}/logo`, {
        method: "POST",
        body: form,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stores"] }),
  });
}

/** Upload slike s uputstvom koja se prikazuje na dnu javne forme za povrat. */
export function useUploadReturnInstruction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const form = new FormData();
      form.append("image", file);
      return fetchJson<{ returnInstructionUrl: string }>(`/api/stores/${id}/return-instruction`, {
        method: "POST",
        body: form,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stores"] }),
  });
}

/** Uklanja sliku s uputstvom (forma se vraća na izgled bez uputstva). */
export function useRemoveReturnInstruction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      fetchJson<{ returnInstructionUrl: string }>(`/api/stores/${id}/return-instruction`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stores"] }),
  });
}
